import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Signed-out demo chooser. The backend returns options only for an explicitly
 * enabled demo deployment and only after validating that each account belongs
 * exclusively to the DEMO organization. The selected credentials still go
 * through the normal password-login flow owned by Login.jsx.
 */
export default function DemoPersonaLogin({ onSelect, disabled, activeRole }) {
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/demo/login-options");
        if (!cancelled && data.password && data.personas?.length) {
          setOptions(data);
        }
      } catch {
        // Feature off, non-demo deployment, or unavailable API: ordinary
        // login remains unchanged and no demo affordance is rendered.
        if (!cancelled) setOptions(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!options) return null;

  return (
    <section
      data-testid="demo-persona-login"
      aria-labelledby="demo-persona-login-title"
      className="mt-6 border-t border-slate-200 pt-5"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id="demo-persona-login-title"
          className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600"
        >
          Explore the demo
        </h2>
        <span className="text-[11px] text-slate-400">One-click sign in</span>
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
        Choose a role to enter its DEMO account.
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {options.personas.map((persona) => (
          <button
            key={persona.role}
            type="button"
            data-testid={`demo-login-${persona.role}`}
            disabled={disabled}
            onClick={() => onSelect(persona, options.password)}
            className="rounded-md border border-slate-200 px-3 py-2 text-left transition-colors hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:cursor-wait disabled:opacity-60"
          >
            <span className="block text-xs font-semibold text-slate-800">
              {activeRole === persona.role ? "Signing in…" : persona.role_label}
            </span>
            <span className="mt-0.5 block truncate text-[11px] text-slate-400">
              {persona.name}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
